import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Countdown } from 'components/common/Countdown';
import { MediaListItem } from 'components/common/MediaListItem';
import { useFirebase } from 'contexts/FirebaseContext';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useLiveEvent } from 'hooks/useLiveEvent';
import { useUser } from 'hooks/useUser';
import { EventOccasion } from 'models/EventOccasion';
import { LiveTrackResult } from 'models/LiveEvent';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { dateExpired } from 'utils/dateExpired';

export function LiveEventRequests() {
  const { t } = useTranslation();
  const { userId, eventId } = useParams();
  const navigate = useNavigate();
  const { db } = useFirebase();
  const auth = getAuth();
  const { user, setUser, requesterId, setRequesterId } = useUser();
  const { submitSearch, listenToSearchResults, submitWish } = useLiveEvent();

  const [event, setEvent] = useState<EventOccasion>();
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<LiveTrackResult[] | null>(null);
  const [activeSearchId, setActiveSearchId] = useState<string | null>(null);
  const [submittedTrack, setSubmittedTrack] = useState<LiveTrackResult | null>(null);
  const [myWishes, setMyWishes] = useState<LiveTrackResult[]>([]);

  const unsubscribeRef = useRef<(() => void) | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const canRequest =
    !dateExpired(event?.expireDate) && !event?.disableRequests && (user?.uid != null || requesterId != null);

  // Authenticate guest anonymously if not logged in
  useEffect(() => {
    if (user?.uid) {
      setRequesterId(user.uid);
    } else {
      signInAnonymously(auth).then((credentials) => {
        setUser(credentials.user);
        setRequesterId(credentials.user.uid);
      });
    }
  }, [user]);

  // Load event details
  const loadEvent = async () => {
    if (userId && eventId) {
      const eventRef = doc(db, `user/${userId}/events`, eventId);
      const eventSnapshot = await getDoc(eventRef);

      if (eventSnapshot.exists()) {
        const eventData = { ...eventSnapshot.data(), id: eventId } as EventOccasion;
        setEvent(eventData);

        // If this event is not a live event, redirect to regular request page
        if (eventData.isLiveEvent === false) {
          navigate(`/${userId}/events/${eventId}/request`, { replace: true });
        }
      }
    }
  };

  useEffect(() => {
    if (userId && eventId) {
      loadEvent();
    }
  }, [userId, eventId]);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!title.trim() && !artist.trim()) return;
    if (!userId || !eventId) return;

    const currentRequesterId = requesterId || user?.uid || '';

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setIsSearching(true);
    setSearchResults(null);
    setSubmittedTrack(null);

    try {
      const searchId = await submitSearch(userId, eventId, currentRequesterId, title, artist);
      setActiveSearchId(searchId);

      // Listen for DJ response in Realtime Database
      unsubscribeRef.current = listenToSearchResults(
        userId,
        eventId,
        searchId,
        (results, status) => {
          if (status === 'completed') {
            setIsSearching(false);
            setSearchResults(results);
            if (unsubscribeRef.current) {
              unsubscribeRef.current();
              unsubscribeRef.current = null;
            }
          }
        }
      );

      // Fallback timeout after 15 seconds if DJ is offline
      searchTimeoutRef.current = setTimeout(() => {
        setIsSearching(false);
        setSearchResults((prev) => (prev === null ? [] : prev));
      }, 15000);
    } catch (err) {
      console.error('Failed to submit live search:', err);
      setIsSearching(false);
    }
  };

  const handleSelectTrack = async (track: LiveTrackResult) => {
    if (!userId || !eventId) return;
    const currentRequesterId = requesterId || user?.uid || '';

    try {
      await submitWish(userId, eventId, currentRequesterId, track, activeSearchId || undefined);
      setSubmittedTrack(track);
      setMyWishes((prev) => [track, ...prev]);
      setSearchResults(null);
      setActiveSearchId(null);
      setTitle('');
      setArtist('');
    } catch (err) {
      console.error('Failed to submit live wish:', err);
    }
  };

  const handleNewSearch = () => {
    setSubmittedTrack(null);
    setSearchResults(null);
    setTitle('');
    setArtist('');
  };

  return (
    <Stack spacing={3} maxWidth="md" sx={{ mx: 'auto', p: 2, maxWidth: '600px' }}>
      {/* Event Header */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h1" gutterBottom textAlign="center">
          {event?.name}
        </Typography>
        {canRequest ? (
          <Alert
            severity="success"
            variant="standard"
            icon={false}
            sx={{
              display: 'inline-flex',
              fontSize: '1rem',
              fontWeight: 'bold',
            }}
          >
            {t('requestsOpenFor', 'Requests open for')}{' '}
            {event?.expireDate && <Countdown targetDate={event.expireDate} />}
          </Alert>
        ) : (
          <Alert
            severity="error"
            variant="outlined"
            sx={{
              display: 'inline-flex',
              fontSize: '1rem',
              fontWeight: 'bold',
            }}
            icon={<ErrorOutlineIcon fontSize="medium" />}
          >
            {t('requestsAreClosed', 'Requests are closed')}
          </Alert>
        )}
      </Box>

      {/* Wish Submitted Success Alert */}
      {submittedTrack && (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon fontSize="large" />}
          action={
            <Button color="inherit" size="small" onClick={handleNewSearch}>
              {t('liveEventNewSearch', 'Another request')}
            </Button>
          }
          sx={{ alignItems: 'center' }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {t('liveEventWishSubmitted', 'Wish submitted!')}
          </Typography>
          <Typography variant="body2">
            {submittedTrack.artist} – {submittedTrack.title}
          </Typography>
        </Alert>
      )}

      {/* Search Input Box */}
      {canRequest && !submittedTrack && (
        <Card elevation={2}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h3" gutterBottom>
              {t('liveEventSearchHeader', 'Live Song Request')}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {t(
                'liveEventSearchDescription',
                'Search the DJ’s music collection directly in real-time.'
              )}
            </Typography>

            <Box component="form" onSubmit={handleSearch}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label={t('title', 'Title')}
                  placeholder={t('liveEventSearchPlaceholderTitle', 'e.g. Billie Jean')}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={isSearching}
                  autoFocus
                />
                <TextField
                  fullWidth
                  label={t('artist', 'Artist')}
                  placeholder={t('liveEventSearchPlaceholderArtist', 'e.g. Michael Jackson')}
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                  disabled={isSearching}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={isSearching || (!title.trim() && !artist.trim())}
                  startIcon={
                    isSearching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />
                  }
                  fullWidth
                >
                  {isSearching
                    ? t('liveEventSearching', 'DJ is searching library...')
                    : t('liveEventSearchButton', 'Search')}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Searching Loader */}
      {isSearching && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1" color="textSecondary">
            {t('liveEventSearching', 'DJ is searching library...')}
          </Typography>
        </Box>
      )}

      {/* Search Results List */}
      {!isSearching && searchResults !== null && (
        <Card elevation={2}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h3" gutterBottom>
              {t('searchResults', 'Search Results')}
            </Typography>

            {searchResults.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  {t(
                    'liveEventNoResults',
                    'No matching tracks found in the DJ’s library. Try a different title or artist.'
                  )}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
                  {t('liveEventSelectTrack', 'Click a track to submit your request:')}
                </Typography>
                {searchResults.map((track, idx) => (
                  <Paper
                    key={track.id || idx}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      transition: 'background-color 0.15s, border-color 0.15s',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderColor: 'primary.main',
                      },
                    }}
                    onClick={() => handleSelectTrack(track)}
                  >
                    <MediaListItem
                      media={
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <MusicNoteIcon />
                        </Box>
                      }
                      title={track.title}
                      subtitle={`${track.artist}${track.album ? ` • ${track.album}` : ''}`}
                      trailing={
                        <IconButton
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectTrack(track);
                          }}
                        >
                          <SendIcon />
                        </IconButton>
                      }
                    />
                  </Paper>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {/* My Submitted Wishes (Session History) */}
      {myWishes.length > 0 && (
        <Card elevation={1}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h3" gutterBottom>
              {t('yourRequestedSongs', 'Your Requests')}
            </Typography>
            <Stack spacing={1} divider={<Divider />}>
              {myWishes.map((w, index) => (
                <Box key={index} sx={{ py: 0.5 }}>
                  <Typography variant="body1" fontWeight={600}>
                    {w.title}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {w.artist}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
